import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  BadgeHelp,
  BookOpen,
  ClipboardCheck,
  CreditCard,
  FileText,
  LayoutDashboard,
  Menu,
  Package,
  ReceiptText,
  Search,
  ShieldCheck,
  ShoppingCart,
  Store,
  Trash2,
  Users,
  WalletCards,
  X,
} from "lucide-react";
import { authStorage } from "../../utils/authStorage";

const roleText = (user) => String(user?.role_name || "").trim().toLowerCase();

const getManualScope = (user, pathname) => {
  const roleName = roleText(user);
  const isAdminManual = pathname.startsWith("/admin");

  if (Number(user?.user_type) === 1) return "admin";
  if (isAdminManual && roleName === "manager") return "manager";
  if (roleName === "supervisor") return "supervisor";
  return "cashier";
};

const manualSections = [
  {
    id: "getting-started",
    title: "Getting Started",
    icon: BookOpen,
    scopes: ["admin", "manager", "supervisor", "cashier"],
    summary: "Understand the workspace, roles, daily flow, and safe sign-in habits.",
    topics: [
      {
        title: "Workspace Basics",
        body: [
          "Use the left sidebar to move between major work areas. The top bar shows the active workspace, notifications, profile actions, settings, billing access when allowed, and this manual.",
          "Admin users manage the whole store workspace. Managers can open the admin side for operational views that are allowed to them. Cashiers and supervisors use the POS side for daily selling and branch work.",
          "If the subscription banner appears, continue normal work unless the app is locked. Billing actions are reserved for the admin owner.",
        ],
      },
      {
        title: "Recommended Daily Routine",
        body: [
          "Before opening the store, confirm the branch, check low stock, review unread notices, and make sure products load with the correct prices.",
          "During the day, keep transactions, expenses, and waste reports updated as they happen. This avoids missing records at closing time.",
          "Before closing, review today’s transactions, compare cash received against receipts, record any final expenses, and report damaged or expired items.",
        ],
      },
      {
        title: "Account Safety",
        body: [
          "Do not share passwords between staff accounts. Each action should be traceable to the person who performed it.",
          "Log out when leaving a shared counter device. If a password reset or forced password change appears, complete it before using the system.",
          "If a staff member leaves the business, the admin should deactivate that user instead of reusing the account.",
        ],
      },
    ],
  },
  {
    id: "cashier-sales",
    title: "Cashier Sales",
    icon: ShoppingCart,
    scopes: ["admin", "manager", "supervisor", "cashier"],
    summary: "Use the POS screen, cart, payments, receipts, and daily transaction records.",
    topics: [
      {
        title: "Creating an Order",
        body: [
          "Open Sales, search or browse products, then add items to the cart. Confirm quantities before payment, especially for duplicate products with similar names.",
          "Use cash or the available payment methods configured for the store. For cash payments, enter the received amount carefully so the change is computed correctly.",
          "Print or save the receipt after payment. If the printer is unavailable, complete the order first and reprint from Transactions when the printer is ready.",
        ],
      },
      {
        title: "Voiding and Corrections",
        body: [
          "Void only when the transaction is truly wrong, duplicated, or cancelled. The system keeps the record for audit and inventory movement history.",
          "Depending on store settings, a manager, supervisor, cashier, or any staff password may be required to approve a paid order void.",
          "If an order is voided after inventory was reduced, the related stock movement should show the reversal in inventory logs.",
        ],
      },
      {
        title: "Cashier End-of-Day Check",
        body: [
          "Open Transactions and review the current day. Compare paid orders, voided orders, payment method totals, and cash received.",
          "Make sure every expense paid from the drawer is recorded in Expenses. Missing expense entries make the cash count look wrong.",
          "Report any waste items before closing so the owner or admin can approve the stock deduction.",
        ],
      },
    ],
  },
  {
    id: "staff-products",
    title: "Products Catalog",
    icon: Package,
    scopes: ["admin", "manager", "supervisor", "cashier"],
    summary: "Find branch products, confirm prices, and understand visible stock.",
    topics: [
      {
        title: "Finding Products Fast",
        body: [
          "Use search for product names, codes, or barcodes. The catalog is meant for quick checking when a customer asks for availability or price.",
          "If a product image is missing, rely on the item name, category, code, and price. Admin users can update product images from the inventory/product maintenance areas.",
          "If a product should be sold but does not appear, check whether the branch has stock released to it or ask an admin to review the product status.",
        ],
      },
      {
        title: "Understanding Branch Stock",
        body: [
          "Cashier stock usually means the stock available for the assigned branch. It may be different from warehouse stock.",
          "A product can be low in a branch but still available in the warehouse. That is a transfer/restock-to-branch situation, not always a supplier purchase.",
          "A product that is low or out in both branch and warehouse is usually a buying priority.",
        ],
      },
    ],
  },
  {
    id: "expenses",
    title: "Expenses",
    icon: WalletCards,
    scopes: ["admin", "manager", "supervisor", "cashier"],
    summary: "Record operating costs and review expense history.",
    topics: [
      {
        title: "Adding an Expense",
        body: [
          "Record expenses as soon as money leaves the drawer or account. Include category, amount, payment method, branch, and a clear description.",
          "For admin users, branch and cashier/admin filters help isolate which location or user created the expense entry.",
          "Use consistent descriptions for repeat costs such as delivery fees, supplies, staff meals, rent, maintenance, and utilities.",
        ],
      },
      {
        title: "Reviewing Expense Records",
        body: [
          "Filter by date range first, then branch or user. This makes reconciliation faster and keeps the table focused.",
          "If the table is empty, confirm that the selected date range includes the day the expense was saved.",
          "Admins should review expenses before final reporting so gross sales and operating costs are interpreted together.",
        ],
      },
    ],
  },
  {
    id: "waste",
    title: "Waste Reports",
    icon: Trash2,
    scopes: ["admin", "manager", "supervisor", "cashier"],
    summary: "Submit, review, approve, or reject expired, damaged, spoiled, and missing stock reports.",
    topics: [
      {
        title: "Submitting Waste",
        body: [
          "Cashiers and staff should report expired, damaged, spoiled, or missing items before stock is deducted.",
          "Add product, quantity, reason, notes, and an image when proof is available. Good notes reduce back-and-forth with the reviewer.",
          "Submitted reports wait for admin review. Do not create duplicate reports unless the first one was rejected and the issue still needs correction.",
        ],
      },
      {
        title: "Reviewing Waste Requests",
        body: [
          "Admins and allowed managers review pending waste requests, confirm the reason and quantity, then approve or reject.",
          "Approving a valid waste request deducts stock and creates a traceable record. Rejecting should be used when the report is duplicated, unclear, or incorrect.",
          "Use the status filter to separate pending work from approved and rejected history.",
        ],
      },
    ],
  },
  {
    id: "admin-dashboard",
    title: "Dashboard",
    icon: LayoutDashboard,
    scopes: ["admin", "manager"],
    summary: "Monitor sales, expenses, low stock, top products, and branch performance.",
    topics: [
      {
        title: "Reading the Overview",
        body: [
          "The dashboard gives a quick health check: gross sales, monthly sales, expenses, low stock, sales trend, and top products.",
          "Use branch scope when you need to compare or focus on a single location. Admin owners can review all branches; managers may see the operational scope available to them.",
          "Treat the dashboard as a starting point. Use Transactions, Inventory, Analytics, or Reports for detailed investigation.",
        ],
      },
      {
        title: "Low Stock Signals",
        body: [
          "Low stock counts depend on the active scope. Warehouse low stock means supplier buying may be needed. Branch low stock may mean a transfer from warehouse is enough.",
          "Out means quantity is zero. Low means quantity is above zero but at or below the configured threshold.",
          "Review the Inventory page before buying because the to-buy checklist separates branch stock from warehouse stock.",
        ],
      },
    ],
  },
  {
    id: "inventory",
    title: "Inventory",
    icon: ClipboardCheck,
    scopes: ["admin", "manager"],
    summary: "Manage products, stock quantities, warehouse scope, branch scope, and to-buy checklists.",
    topics: [
      {
        title: "Warehouse vs Branch Scope",
        body: [
          "Warehouse scope shows central stock available before releasing items to branches. Branch scope shows stock currently available in that branch.",
          "Use warehouse scope when planning supplier purchases. Use branch scope when deciding what to transfer or release to a location.",
          "A product can be urgent for a branch but not urgent for supplier buying if the warehouse still has enough stock.",
        ],
      },
      {
        title: "To-Buy Checklist",
        body: [
          "Use the checklist to pick only the items the owner plans to buy now. The selection is saved in the browser so a refresh does not clear it.",
          "Copy creates a phone-friendly text list. Share opens the device share sheet when supported. PDF creates a printable list with counters and checkboxes.",
          "Collapse the checklist when you need more room to inspect the product table. The selected count remains visible.",
        ],
      },
      {
        title: "Sorting and Filtering",
        body: [
          "Filter All Items, Low Stock, Out of Stock, or Healthy Stock depending on the review task.",
          "Sort low-to-high to find the most urgent stock gaps first. Sort high-to-low when checking overstock or healthy inventory.",
          "Search narrows the visible list while keeping the stock filter and sort order applied.",
        ],
      },
    ],
  },
  {
    id: "admin-products",
    title: "Product Maintenance",
    icon: Package,
    scopes: ["admin", "manager"],
    summary: "Create products, edit details, maintain prices, images, barcodes, and availability.",
    topics: [
      {
        title: "Product Records",
        body: [
          "Keep product names short but specific. Include size, flavor, variant, or pack count when those details affect selling.",
          "Add images for demo and daily use because they reduce cashier selection mistakes. Use clear product photos or generated placeholders only when real images are unavailable.",
          "Use categories consistently so search, reports, and product browsing stay organized.",
        ],
      },
      {
        title: "Price Changes",
        body: [
          "When prices change, verify whether the change applies to all branches or only one branch.",
          "Price logs help explain why a sale used a certain price on a certain date.",
          "Avoid changing prices during active checkout unless staff are aware, because open carts may need review.",
        ],
      },
    ],
  },
  {
    id: "transactions",
    title: "Transactions",
    icon: ReceiptText,
    scopes: ["admin", "manager", "supervisor", "cashier"],
    summary: "Review paid and voided orders, filter by date/status, and reprint receipts.",
    topics: [
      {
        title: "Finding Orders",
        body: [
          "Use date filters first, then status, cashier, branch, or order search. This keeps large transaction histories manageable.",
          "Admins and managers can review broader date ranges. Cashier views are focused on daily branch work.",
          "When checking a customer concern, search by order number or use the exact date and approximate time.",
        ],
      },
      {
        title: "Reprints and Audit",
        body: [
          "Receipt reprints should match the saved order. If the receipt data looks wrong, check the transaction details before giving it to a customer.",
          "Voided transactions stay visible because they are part of the audit trail.",
          "Use the cashier and branch columns to understand who created the order and where it happened.",
        ],
      },
    ],
  },
  {
    id: "users",
    title: "Users and Roles",
    icon: Users,
    scopes: ["admin"],
    summary: "Create, update, deactivate, and understand staff access.",
    topics: [
      {
        title: "Role Meaning",
        body: [
          "Cashiers handle sales, daily transactions, expenses, waste reporting, and product lookup in the POS side.",
          "Supervisors use staff tools and may be authorized for operational approvals depending on store settings.",
          "Managers can open selected admin-side operations but should not receive owner-only access such as full user administration or billing.",
        ],
      },
      {
        title: "Managing Accounts",
        body: [
          "Create one account per person. Assign the correct branch and role so activity is traceable.",
          "Deactivate users who no longer work in the store. Do not recycle accounts for a different person.",
          "When a user forgets a password, use the proper reset process and require them to change it after login.",
        ],
      },
    ],
  },
  {
    id: "analytics-reports",
    title: "Analytics and Reports",
    icon: FileText,
    scopes: ["admin", "manager"],
    summary: "Analyze performance, export CSV/PDF reports, and reconcile sales.",
    topics: [
      {
        title: "Analytics",
        body: [
          "Use analytics to review sales trends, top products, branch performance, and month or year views.",
          "The month picker can focus one month or all months in a year when yearly context is needed.",
          "Compare analytics with inventory and expense records before making buying or staffing decisions.",
        ],
      },
      {
        title: "Reports and Exports",
        body: [
          "Reports support date ranges, branch filters, and cashier filters. Apply the correct range before downloading.",
          "CSV is best for spreadsheet work. PDF is best for printing, sharing, and quick owner review.",
          "Transaction PDFs include order number, date, time, branch, cashier, status, payment, items, totals, received amount, change, and seat details when available.",
        ],
      },
    ],
  },
  {
    id: "settings-billing",
    title: "Settings and Billing",
    icon: CreditCard,
    scopes: ["admin"],
    summary: "Configure profile, branding, operations, printer settings, subscription, and billing.",
    topics: [
      {
        title: "Settings",
        body: [
          "Use Settings to update profile details, workspace branding, appearance, operational preferences, and hardware printer setup.",
          "Operational preferences include who can authorize voids. Choose a policy that matches the store’s control process.",
          "Printer settings should be tested after changes so receipts do not fail during checkout.",
        ],
      },
      {
        title: "Billing",
        body: [
          "Billing is owner/admin-only. Managers and staff should contact the admin owner if the workspace is near expiry or paused.",
          "The subscription banner warns before expiry. Renew early to avoid interruption during store hours.",
          "Payment history and renewal notices should be reviewed from the billing modal when available.",
        ],
      },
    ],
  },
  {
    id: "android-bridge-printing",
    title: "Android Bridge and Printing",
    icon: ReceiptText,
    scopes: ["admin", "manager", "supervisor", "cashier"],
    summary: "Connect Android Bridge, print receipts and barcode labels, check readiness, and recover from printing issues.",
    topics: [
      {
        title: "What Android Bridge Does",
        body: [
          "Android Bridge lets the web POS send local print jobs to the store phone or tablet, then the Android device sends them to the receipt printer or barcode label printer.",
          "Use Android Bridge for SaaS stores because the browser and cloud server normally cannot directly reach printers inside the store network.",
          "The bridge must be installed, opened, and connected before receipts or barcode labels can print. Sales can still be recorded if printing fails, then receipts can be reprinted later from Transactions.",
        ],
      },
      {
        title: "Install and Start the Bridge",
        body: [
          "Download the Android Print Bridge APK from Settings > Hardware Printer, then install it on the phone or tablet that stays at the counter.",
          "Keep the Android device on the same Wi-Fi network as network printers. For USB or Bluetooth printers, keep the printer connected or paired to the same Android device.",
          "Allow the permissions requested by Android Bridge, such as local network, Bluetooth or nearby devices, notifications, and storage when required by the device.",
          "Open Android Bridge before store opening and leave it running while the cashier is selling.",
        ],
      },
      {
        title: "Receipt Printer Setup",
        body: [
          "In Settings > Hardware Printer, choose the correct Branch Scope. Branch-specific printer settings override the company default for cashiers assigned to that branch.",
          "Set Connection Mode to Android Print Bridge for normal SaaS use. Direct ESC/POS should only be used when the backend server is inside the same store network as the printer.",
          "Enter the receipt printer IP and port for network printers, then choose the receipt paper width, usually 58mm or 80mm depending on the printer paper.",
          "Use Auto cut receipt only when the printer supports cutting. Enable Cash drawer connected and Open drawer for cash payments only when a cash drawer is physically attached.",
          "Save settings first, then use Test Connection and Print Test before using the counter for real sales.",
        ],
      },
      {
        title: "Bridge Network Status",
        body: [
          "Bridge Network Status shows the Android device IP, gateway, network prefix, last report time, and detected printer IPs reported from inside the store network.",
          "If no printer IP is detected, confirm the printer is powered on, connected to the same Wi-Fi or LAN, and not isolated on a different router or guest network.",
          "Use the detected printer IP as a guide, but confirm it is the correct receipt printer before saving.",
          "If Last Report is old or empty, open Android Bridge again on the device and check that the device has internet and local network access.",
        ],
      },
      {
        title: "Barcode Label Printer Setup",
        body: [
          "Barcode label printing also uses Android Bridge. The product barcode tool sends a TSPL or TSPL2 label job to the bridge.",
          "For an Xprinter XP-236B or similar label printer, configure the barcode printer transport inside Android Bridge as USB, Bluetooth, or Network, depending on how the printer is connected.",
          "The barcode print screen uses a 50mm x 30mm Xprinter 236B preset by default, with adjustable label width, label height, barcode height, and copies.",
          "Use labels that match the configured size. If the physical sticker is different from the selected size, the barcode may print cropped, too small, or misaligned.",
          "Print one test label before printing many copies, then scan it with the POS scanner or phone scanner to confirm it reads correctly.",
        ],
      },
      {
        title: "Printing Product Barcodes",
        body: [
          "Open the product barcode print action from the product list or product maintenance area.",
          "A product must have a product code or barcode value. Empty barcodes or unsupported characters cannot be printed.",
          "Confirm the product name, price, barcode preview, label size, barcode height, and number of copies before pressing Print Label.",
          "Use Code 128-compatible values for reliable barcode printing and scanning.",
          "If labels queue but do not print, check Android Bridge first, then check printer power, paper roll or label roll direction, USB/Bluetooth/network connection, and the selected transport in the bridge app.",
        ],
      },
      {
        title: "Daily Cashier Check",
        body: [
          "Before opening the counter, confirm the Android device is charged, connected to the correct network, and paired or connected to the receipt printer.",
          "Open Android Bridge and confirm it shows a ready or connected state before processing the first sale.",
          "Run a quick receipt test when possible. If barcode labels are used that day, print one test label too.",
          "Keep spare receipt paper and barcode labels near the counter so printing problems do not stop the line.",
        ],
      },
      {
        title: "When Receipt Printing Fails",
        body: [
          "First check that Android Bridge is open and connected. Then check printer power, paper, Bluetooth or network connection, and device battery.",
          "Do not recreate the sale just because printing failed. Find the paid order in Transactions and use reprint when the printer is ready.",
          "If the cash drawer does not open, confirm cash drawer settings are enabled and that the drawer cable is connected to the receipt printer.",
          "If printing repeatedly fails, record the order number and payment method, then ask the admin or manager to check Android Bridge and printer settings.",
        ],
      },
      {
        title: "Offline or Weak Internet Situations",
        body: [
          "If the app is still open but internet is unstable, avoid refreshing the page unless instructed by the admin.",
          "If a sale cannot be completed online, write down the transaction manually and enter it once the connection is restored according to store policy.",
          "For restocking trips, use the copied checklist or downloaded PDF saved on the phone so the owner can still buy items without internet.",
        ],
      },
      {
        title: "Admin Maintenance Checklist",
        body: [
          "Keep one assigned Android device for printing when possible. Frequent device changes make printer setup harder to track.",
          "After changing router, Wi-Fi name, printer IP, branch assignment, paper width, or cash drawer settings, save the settings and run both Test Connection and Print Test.",
          "Check branch scope carefully before saving. A wrong branch override can make one branch print correctly while another branch fails.",
          "Train staff to reprint paid orders from Transactions instead of creating duplicate sales when receipts fail.",
          "For barcode printers, keep a sample label with the correct width and height beside the printer so admins can match the print screen settings quickly.",
        ],
      },
    ],
  },
  {
    id: "quick-faq",
    title: "FAQ",
    icon: BadgeHelp,
    scopes: ["admin", "manager", "supervisor", "cashier"],
    summary: "Answers to common operational questions.",
    topics: [
      {
        title: "Why can stock be low in one place but not another?",
        body: [
          "Warehouse and branch quantities are separate. Branch low stock means the branch needs more items. Warehouse low stock means the business may need to buy from a supplier.",
          "Check both values before deciding whether to buy, transfer, or wait.",
        ],
      },
      {
        title: "Why does a selected date show no records?",
        body: [
          "The most common cause is the date range. Confirm the From and To dates, then check branch, cashier, status, and search filters.",
          "If the record was just created, refresh the page or reapply the filter.",
        ],
      },
      {
        title: "What should staff do when internet is unstable?",
        body: [
          "Keep manual notes of sales, expenses, waste, and stock concerns until the connection returns.",
          "For buying trips, use the copied to-buy checklist or downloaded PDF saved on the phone.",
        ],
      },
    ],
  },
];

const scopeLabels = {
  admin: "Admin Manual",
  manager: "Manager Manual",
  supervisor: "Supervisor Manual",
  cashier: "Cashier Manual",
};

const scopeDescriptions = {
  admin: "Full documentation for admin owners, including manager, supervisor, and cashier workflows.",
  manager: "Admin-side operations available to managers, plus staff workflows they may need to support.",
  supervisor: "Staff documentation for supervisors, including cashier workflows and operational approvals.",
  cashier: "Cashier documentation focused on selling, expenses, waste reports, and daily branch work.",
};

const matchesSearch = (section, query) => {
  if (!query) return true;
  const haystack = [
    section.title,
    section.summary,
    ...section.topics.flatMap((topic) => [topic.title, ...topic.body]),
  ].join(" ").toLowerCase();
  return haystack.includes(query.toLowerCase());
};

function ManualSidebar({
  query,
  setQuery,
  visibleSections,
  onNavigate,
  className = "",
  navClassName = "max-h-[calc(100vh-190px)] overflow-y-auto",
  showSearch = true,
}) {
  return (
    <div className={`min-w-0 rounded-[24px] border border-slate-200 bg-white p-3 shadow-[0_18px_45px_rgba(15,23,42,0.07)] sm:p-4 ${className}`}>
      {showSearch && (
        <label className="relative block">
          <Search className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search manual..."
            className="h-12 w-full rounded-2xl border border-slate-200 bg-white pl-12 pr-4 text-sm text-slate-900 outline-none transition focus:border-[var(--color-primary)] focus:ring-4 focus:ring-[color-mix(in_srgb,var(--color-primary)_14%,transparent)]"
          />
        </label>
      )}

      <nav className={`scrollbar-theme ${showSearch ? "mt-4" : ""} space-y-1 pr-1 ${navClassName}`}>
        {visibleSections.map((section) => {
          const Icon = section.icon || Store;
          return (
            <a
              key={section.id}
              href={`#${section.id}`}
              onClick={onNavigate}
              className="group flex min-w-0 gap-3 rounded-2xl px-2.5 py-3 text-left transition hover:bg-slate-50 sm:px-3"
            >
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-500 group-hover:text-slate-900 sm:h-10 sm:w-10">
                <Icon className="h-4.5 w-4.5 sm:h-5 sm:w-5" />
              </span>
              <span className="min-w-0">
                <span className="block text-sm font-semibold text-slate-900">{section.title}</span>
                <span className="mt-0.5 line-clamp-2 block text-xs leading-5 text-slate-500">{section.summary}</span>
              </span>
            </a>
          );
        })}
      </nav>
    </div>
  );
}

export default function UserManualPage() {
  const [query, setQuery] = useState("");
  const [manualMenuOpen, setManualMenuOpen] = useState(false);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const location = useLocation();
  const user = authStorage.getUser();
  const scope = getManualScope(user, location.pathname);

  const visibleSections = useMemo(
    () => manualSections
      .filter((section) => section.scopes.includes(scope))
      .filter((section) => matchesSearch(section, query.trim())),
    [query, scope]
  );

  useEffect(() => {
    if (!manualMenuOpen) return undefined;

    const previousBodyOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;
    };
  }, [manualMenuOpen]);

  const firstSectionId = visibleSections[0]?.id;

  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 320);
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <main className="min-h-screen overflow-x-hidden bg-slate-50 px-3 pb-4 pt-20 text-slate-900 sm:px-6 sm:pb-5 lg:px-8 lg:pt-5">
      <div className="mx-auto w-full max-w-[1600px] min-w-0">
      <div className="fixed inset-x-0 top-0 z-[60] border-b border-slate-200/80 bg-slate-50/95 px-3 py-3 shadow-[0_14px_32px_rgba(15,23,42,0.08)] backdrop-blur lg:hidden">
        <button
          type="button"
          onClick={() => setManualMenuOpen(true)}
          className="mx-auto flex h-12 w-full max-w-[1600px] items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 text-left text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
          aria-label="Open manual menu"
        >
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-[var(--color-primary)] text-white">
            <Menu className="h-4 w-4" />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-xs font-bold uppercase tracking-[0.18em] text-slate-500">User Manual</span>
            <span className="block truncate text-sm font-bold text-slate-950">Manual Sections</span>
          </span>
          <span className="shrink-0 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] font-bold text-slate-500">
            {visibleSections.length}
          </span>
        </button>
      </div>

      <div
        className={`fixed inset-0 z-[70] bg-black/30 backdrop-blur-sm transition-opacity duration-300 ease-out lg:hidden ${
          manualMenuOpen ? "pointer-events-auto opacity-100" : "pointer-events-none opacity-0"
        }`}
        onClick={() => setManualMenuOpen(false)}
        aria-hidden="true"
      />
      <aside
        className={`fixed inset-y-0 left-0 z-[80] h-[100dvh] w-[min(22rem,88vw)] transform transition-transform duration-300 ease-out will-change-transform lg:hidden ${
          manualMenuOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="relative flex h-full max-h-[100dvh] flex-col overflow-hidden rounded-r-[28px] border-r border-white/70 bg-white shadow-[0_28px_70px_rgba(15,23,42,0.22)]">
          <div className="border-b border-slate-200/80 bg-white px-4 py-4 shadow-[0_10px_22px_rgba(15,23,42,0.05)]">
            <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-bold text-slate-950">Manual Sections</p>
              <p className="truncate text-xs text-slate-500">{scopeLabels[scope]}</p>
            </div>
            <button
              type="button"
              onClick={() => setManualMenuOpen(false)}
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-500 shadow-sm transition hover:bg-slate-50 hover:text-slate-900"
              aria-label="Close manual menu"
            >
              <X className="h-5 w-5" />
            </button>
            </div>
            <label className="relative mt-4 block">
              <Search className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search manual..."
                className="h-12 w-full rounded-2xl border border-slate-200 bg-white pl-12 pr-4 text-sm text-slate-900 outline-none transition focus:border-[var(--color-primary)] focus:ring-4 focus:ring-[color-mix(in_srgb,var(--color-primary)_14%,transparent)]"
              />
            </label>
          </div>
          <div className="scrollbar-theme min-h-0 flex-1 overflow-y-auto p-4">
            <ManualSidebar
              query={query}
              setQuery={setQuery}
              visibleSections={visibleSections}
              onNavigate={() => setManualMenuOpen(false)}
              className="border-0 shadow-none"
              navClassName="overflow-visible"
              showSearch={false}
            />
          </div>
        </div>
      </aside>

      <div className="hidden lg:fixed lg:bottom-5 lg:left-8 lg:top-5 lg:z-20 lg:block lg:w-[340px]">
        <ManualSidebar
          query={query}
          setQuery={setQuery}
          visibleSections={visibleSections}
          className="h-full overflow-hidden"
          navClassName="max-h-[calc(100vh-150px)] overflow-y-auto"
        />
      </div>

      <div className="min-w-0 lg:pl-[365px]">
      <section className="min-w-0 rounded-[24px] border border-slate-200 bg-white p-4 shadow-[0_20px_60px_rgba(15,23,42,0.08)] sm:rounded-[28px] sm:p-8">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div className="min-w-0">
            <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-xs font-bold uppercase tracking-[0.22em] text-slate-500 shadow-sm">
              <BookOpen className="h-4 w-4" />
              User Manual
            </div>
            <h1 className="mt-5 text-3xl font-bold text-slate-950 sm:text-5xl">
              {scopeLabels[scope]}
            </h1>
            <p className="mt-3 max-w-3xl text-sm leading-6 text-slate-600 sm:text-base sm:leading-7">
              {scopeDescriptions[scope]}
            </p>
          </div>
          <div className="flex min-w-0 items-center gap-2 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
            <ShieldCheck className="h-5 w-5 text-slate-500" />
            <span className="min-w-0 truncate">{visibleSections.length} sections available</span>
          </div>
        </div>
      </section>

      <div className="mt-4 grid min-w-0 gap-4 sm:mt-5 sm:gap-5">
        <main className="min-w-0 space-y-4 sm:space-y-5">
          {visibleSections.length === 0 ? (
            <div className="rounded-[24px] border border-slate-200 bg-white p-10 text-center shadow-sm">
              <Search className="mx-auto h-10 w-10 text-slate-300" />
              <h2 className="mt-4 text-xl font-semibold text-slate-950">No manual section found</h2>
              <p className="mt-2 text-sm text-slate-500">Try a different keyword or clear the search box.</p>
            </div>
          ) : (
            visibleSections.map((section) => {
              const Icon = section.icon || Store;
              return (
                <article
                  key={section.id}
                  id={section.id}
                  className="min-w-0 scroll-mt-5 rounded-[24px] border border-slate-200 bg-white p-4 shadow-[0_18px_50px_rgba(15,23,42,0.07)] sm:rounded-[28px] sm:p-7"
                >
                  <div className="flex min-w-0 items-start gap-3 sm:gap-4">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-[var(--color-primary)] text-white shadow-[0_14px_30px_rgba(15,23,42,0.16)] sm:h-12 sm:w-12">
                      <Icon className="h-5 w-5 sm:h-6 sm:w-6" />
                    </span>
                    <div className="min-w-0">
                      <h2 className="break-words text-xl font-bold text-slate-950 sm:text-2xl">{section.title}</h2>
                      <p className="mt-1 text-sm leading-6 text-slate-500">{section.summary}</p>
                    </div>
                  </div>

                  <div className="mt-5 min-w-0 space-y-4 sm:mt-6 sm:space-y-5">
                    {section.topics.map((topic) => (
                      <section key={topic.title} className="min-w-0 rounded-2xl border border-slate-100 bg-slate-50/70 p-3 sm:p-5">
                        <h3 className="break-words text-base font-semibold text-slate-950 sm:text-lg">{topic.title}</h3>
                        <ul className="mt-3 space-y-2">
                          {topic.body.map((line) => (
                            <li key={line} className="flex gap-3 text-sm leading-6 text-slate-600">
                              <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--color-primary)]" />
                              <span>{line}</span>
                            </li>
                          ))}
                        </ul>
                      </section>
                    ))}
                  </div>
                </article>
              );
            })
          )}

        </main>
      </div>
      </div>
      {firstSectionId && showBackToTop && (
        <Link
          to="#"
          onClick={(event) => {
            event.preventDefault();
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
          className="fixed bottom-5 right-5 z-50 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-600 shadow-[0_14px_35px_rgba(15,23,42,0.16)] transition hover:bg-slate-50 sm:bottom-6 sm:right-6"
        >
          Back to top
        </Link>
      )}
      </div>
    </main>
  );
}
